/**
 * จุดเดียวของทั้งแอปที่แนบ `Authorization: Bearer` และดัก `401`
 *
 * ทำไมต้องเป็นจุดเดียว: client ของแต่ละโดเมน (`lib/permissions`, `lib/products`
 * และตัวที่ยังไม่ได้เขียน) ต่างก็ประกอบ request เอง ถ้าปล่อยให้แต่ละตัวเติม header
 * เองจะมีวันหนึ่งที่ client ตัวใหม่ลืม แล้วอาการที่เห็นคือ "หน้าจอนั้นใช้ไม่ได้"
 * ซึ่งวินิจฉัยยากกว่าที่ควรมาก ที่นี่จึงเป็น `fetch` ค่าเริ่มต้นของ client ทุกตัว —
 * client ที่ไม่ระบุ `fetchImpl` จะได้ token ไปด้วยเสมอโดยไม่ต้องรู้ว่ามี token อยู่
 *
 * และเป็นจุดเดียวที่ดัก `401` ด้วยเหตุผลเดียวกัน: D-049 กำหนดว่า `401` จาก
 * endpoint **ใดก็ตาม** ต้องล้าง token ทิ้ง ไม่ใช่เฉพาะ endpoint สิทธิ์ — session ที่
 * ถูก revoke แล้ว (เช่น ผู้ดูแลระบบรีเซ็ตรหัสจนฆ่า session ตาม D-048) ต้องไม่ทิ้ง
 * แอปไว้ในสถานะกึ่งเข้าสู่ระบบ
 */

import { endSession, getSessionState } from './session';

export const AUTHORIZATION_HEADER = 'Authorization';

/** ประกอบค่า header ตาม RFC 6750 — รวมไว้ที่เดียวเพื่อให้เทสต์อ้างค่าเดียวกันได้ */
export function bearerValue(token: string): string {
  return `Bearer ${token}`;
}

/**
 * ห่อ `fetch` ให้แนบ token และล้าง session เมื่อเจอ `401`
 *
 * `baseFetch` ถูกอ่านตอนเรียกจริง ไม่ใช่ตอนสร้าง เพื่อให้เทสต์ที่ stub
 * `globalThis.fetch` ภายหลังยังถูกใช้จริง และเพื่อให้ runtime ที่ไม่มี `fetch`
 * ล้มเหลวแบบที่ client แปลงเป็น `network-error` ได้ตามเดิม
 */
export function createAuthorizedFetch(baseFetch?: typeof fetch): typeof fetch {
  return async (input, init) => {
    const target = baseFetch ?? globalThis.fetch;
    if (typeof target !== 'function') {
      throw new TypeError('fetch ไม่พร้อมใช้งานใน runtime นี้');
    }

    const { token } = getSessionState();
    const headers = new Headers(init?.headers);
    if (token !== null) {
      headers.set(AUTHORIZATION_HEADER, bearerValue(token));
    }

    const response = await target(input, { ...init, headers });

    if (response.status === 401) {
      endSession('UNAUTHORIZED');
    }
    return response;
  };
}

/** `fetch` ค่าเริ่มต้นของ client ทุกตัวในแอป */
export const apiFetch: typeof fetch = createAuthorizedFetch();
